import {
  RequirementExpiryType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { formatDate } from "@clipboard-health/date-time";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { parseISO } from "date-fns";
import { type ReactNode } from "react";

import { DocumentStatusCard } from "../components/StatusCard";
import { DocumentStatusCardActionButton } from "../components/StatusCardActionButton";
import { useOpenDocument } from "../useOpenDocument";
import { shouldShowPendingReason } from "../utils/shouldShowPendingReason";
import { shouldShowRejectionReason } from "../utils/shouldShowRejectionReason";
import { workerRequirementStatusToDocumentCardStatus } from "../utils/workerRequirementStatusToDocumentCardStatus";
import { useRequirementDetailsContext } from "./useRequirementDetailsContext";

interface DocumentDetailsStatusCardProps {
  hideViewDocument?: boolean;
  footer?: ReactNode;
}

export function DocumentDetailsStatusCard(props: DocumentDetailsStatusCardProps) {
  const { hideViewDocument = false, footer } = props;
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const { status, documents, expiry, requirement } = workerRequirement;
  const document = documents?.[0];
  const { id: documentId = "" } = document ?? {};
  const { fileStorageFileKey, fileStorageRootFolder } = document?.attributes ?? {};

  const documentCardStatus = workerRequirementStatusToDocumentCardStatus[status];
  const { onClick: openDocument, isLoading: isOpeningDocument } = useOpenDocument();

  const getStatusMessage = () => {
    if (
      (status === WorkerRequirementStatus.REJECTED && shouldShowRejectionReason(document)) ||
      (status === WorkerRequirementStatus.IN_REVIEW && shouldShowPendingReason(document))
    ) {
      return document?.attributes?.notification?.note ?? document?.attributes?.notification?.reason;
    }

    return undefined;
  };

  const statusMessage = getStatusMessage();
  const areFileKeysPresent = isDefined(fileStorageFileKey) && isDefined(fileStorageRootFolder);

  if (!isDefined(documentCardStatus)) {
    return null;
  }

  const shouldShowExpiry =
    status === WorkerRequirementStatus.APPROVED &&
    isDefined(expiry) &&
    requirement?.attributes.expiryType === RequirementExpiryType.EXPIRY_DATE;

  return (
    <DocumentStatusCard
      variant={documentCardStatus}
      statusMessage={statusMessage}
      expirationDate={parseISO(expiry ?? "")}
      isLoading={isOpeningDocument}
    >
      <Stack direction="column" spacing={2}>
        <Stack direction="column" spacing={4}>
          <Text variant="body2">
            {shouldShowExpiry && `Expires ${formatDate(new Date(expiry))}`}
          </Text>

          {isDefined(documentId) && areFileKeysPresent && !hideViewDocument && (
            <DocumentStatusCardActionButton
              onClick={async () => {
                await openDocument(documentId);
              }}
            />
          )}
        </Stack>

        {footer}
      </Stack>
    </DocumentStatusCard>
  );
}
